import { createPortal } from 'react-dom';
import { useConfig } from '../context/ConfigContext';
import { getCourtRateBands, isCourtAvailable } from '../utils/bookingUtils';
import '../css/SportPickerModal.css';
import { formatMoney } from '../utils/currencyUtil';

/**
 * "Which court would you like to book?" — shown when the club offers more
 * than one sport (Config -> Sports & Courts has 2+ entries) and the guest
 * taps any "Book a Court" button. Picking a card sets the active sport
 * (see App.jsx's handleSportChosen) and continues straight into the
 * existing TimeSlotSelection flow, now scoped to that sport's courts.
 *
 * A single-sport club never sees this — App.jsx's handleBookNow skips
 * straight to booking when config.sports has only one entry, so this
 * component simply never mounts for them.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {Array<{id: string, name: string, icon: string}>} sports
 * @param {(sportId: string) => void} onSelect
 */
function SportPickerModal({ isOpen, onClose, sports, onSelect }) {
  const { config } = useConfig();

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  /**
   * "6 courts · from ₱300/hr" for one sport's card. Counts only courts a
   * guest can actually book (Config -> Sports & Courts -> Available), and
   * takes the cheapest of every day/night rate across them so the "from"
   * price is a real floor rather than an average. Falls back through the
   * same chain the booking grid uses: a court's own override, then the
   * sport's rates, then the legacy club-wide ones.
   */
  const sportMeta = (sport) => {
    const sportCourts = (config?.courts || [])
      .filter(c => c.sportId === sport.id && isCourtAvailable(config, c.number));
    const rates = [];
    sportCourts.forEach((c) => {
      // Every band's effective price for this court, from the shared
      // resolver — so "from ₱x" stays a real floor once a club has more
      // than the original two rates.
      getCourtRateBands(config, c.number, sport.id).forEach(({ price }) => {
        if (Number.isFinite(price) && price > 0) rates.push(price);
      });
    });
    const from = rates.length > 0 ? Math.min(...rates) : null;
    const countLabel = `${sportCourts.length} court${sportCourts.length === 1 ? '' : 's'}`;
    return from ? `${countLabel} · from ${formatMoney(from, config)}/hr` : countLabel;
  };

  return createPortal(
    <div className="spm-overlay" onClick={handleBackdropClick}>
      <div className="spm-card">
        <button type="button" className="spm-close" onClick={onClose} aria-label="Close">&times;</button>
        <h2 className="spm-title">Which court would you like to book?</h2>
        <p className="spm-subtitle">Choose a sport and we will take you to its booking page.</p>
        <div className="spm-grid">
          {/* Deliberately a <div role="button"> rather than a real <button>:
              global.css has a blanket `button { max-height: 50px; }` rule,
              which clipped these multi-row option cards to the height of
              their icon and pushed the name/meta lines outside the card's
              own border. Same reason OpenPlaySessionCard.jsx avoids it. */}
          {sports.map(sport => (
            <div
              key={sport.id}
              role="button"
              tabIndex={0}
              className="spm-option"
              onClick={() => onSelect(sport.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(sport.id);
                }
              }}
            >
              <span className="spm-option-icon">{sport.icon || '🎾'}</span>
              <span className="spm-option-name">{sport.name}</span>
              <span className="spm-option-meta">{sportMeta(sport)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SportPickerModal;
